import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * PPZ Shipping Progress
 *
 * Muestra cuánto falta para el envío gratis: (precio de la variante ×
 * cantidad a añadir) + lo que ya hay en el carrito. También añade ese
 * mismo precio pendiente al texto del botón "Añadir al carrito".
 *
 * El total del carrito arranca con el valor renderizado por Liquid
 * (`data-cart-total`, en céntimos) y se resincroniza en vivo cada vez que
 * el carrito cambia por AJAX en cualquier parte de la página (drawer,
 * sticky-add-to-cart, otra ficha, etc.), escuchando el mismo evento
 * estándar que ya usa el resto del tema (cart-drawer.js, header-actions.js).
 *
 * @extends {Component}
 */
export class PpzShippingProgress extends Component {
  requiredRefs = ['fill', 'text'];

  #controller = new AbortController();
  #cartTotal = 0;
  #threshold = 0;
  #variantPrice = 0;
  #quantity = 1;

  connectedCallback() {
    super.connectedCallback();

    this.#cartTotal = Number(this.dataset.cartTotal) || 0;
    this.#threshold = Number(this.dataset.threshold) || 0;
    this.#variantPrice = Number(this.dataset.variantPrice) || 0;

    const { signal } = this.#controller;
    const section = this.closest('.shopify-section, dialog');

    section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    section?.addEventListener('quantity-selector:update', this.#handleQuantityUpdate, { signal });
    // En document, no en `section`: el carrito puede cambiar desde el drawer,
    // el sticky-add-to-cart o cualquier otra parte de la página, no solo
    // desde esta sección.
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, { signal });

    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /** @param {CustomEvent & {promise: Promise<any>}} event */
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail?.resource) return;
        this.#variantPrice = detail.resource.price;
        this.#render();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[ppz-shipping-progress] Event promise rejected:', error);
      });
  };

  /** @param {CustomEvent<{quantity: number}>} event */
  #handleQuantityUpdate = (event) => {
    const quantity = event.detail?.quantity;
    if (typeof quantity !== 'number') return;
    this.#quantity = quantity;
    this.#render();
  };

  /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
  #handleCartUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        const amount = cart?.cost?.totalAmount?.amount;
        if (amount === undefined) return;
        // cart.cost.totalAmount.amount llega en unidades decimales (p.ej.
        // "59.90"), no en céntimos como data-cart-total/data-variant-price —
        // hay que convertir para que la suma con #variantPrice cuadre.
        this.#cartTotal = Math.round(Number(amount) * 100);
        this.#render();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[ppz-shipping-progress] Cart update promise rejected:', error);
      });
  };

  #render() {
    const pending = this.#variantPrice * this.#quantity;
    const total = this.#cartTotal + pending;
    const remaining = Math.max(0, this.#threshold - total);
    const percent = this.#threshold > 0 ? Math.min(100, (total / this.#threshold) * 100) : 100;

    this.refs.fill.style.width = `${percent}%`;
    this.refs.text.textContent =
      remaining > 0 ? `Te faltan ${this.#formatMoney(remaining)} para el envío gratis` : '¡Envío gratis conseguido!';

    this.#updateButtonPrice(pending);
  }

  /** @param {number} pending */
  #updateButtonPrice(pending) {
    const button = this.closest('.ppz-page__info')?.querySelector('[ref="addToCartButton"]');
    if (!button) return;

    let priceLabel = button.querySelector('.ppz-cart-price');
    if (!priceLabel) {
      priceLabel = document.createElement('span');
      priceLabel.className = 'ppz-cart-price';
      button.appendChild(priceLabel);
    }
    priceLabel.textContent = this.#formatMoney(pending);
  }

  /** @param {number} cents */
  #formatMoney(cents) {
    return (cents / 100).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  }
}

if (!customElements.get('ppz-shipping-progress')) {
  customElements.define('ppz-shipping-progress', PpzShippingProgress);
}
